import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { ContractMessage, ContractState } from '../../../types/ContractMessage';
import { PriceMessage } from '../../../types/PricesMessage';
import { GetPricesParams } from '../../common/api/clients/gateway.api';
import { queries } from '../../common/api/queryKeys/queries';

interface PriceMap {
    [portId: string]: PriceMessage[];
}

export const sortPriceCompareCards = (prices: PriceMessage[], defaultContractId?: string): PriceMessage[] => {
    return prices.toSorted((price, otherPrice) => {
        if (defaultContractId) {
            if (price.contractId === defaultContractId && otherPrice.contractId !== defaultContractId) {
                return -1;
            }

            if (price.contractId !== defaultContractId && otherPrice.contractId === defaultContractId) {
                return 1;
            }
        }

        return price.supplier.supplierGroup.name.localeCompare(otherPrice.supplier.supplierGroup.name);
    });
};

const makePriceMap = (prices: PriceMessage[], defaultContractId?: string): PriceMap => {
    const map: PriceMap = {};
    prices.forEach((price) => {
        if (!map[price.portId]) {
            map[price.portId] = [];
        }
        map[price.portId].push(price);
    });

    Object.keys(map).forEach((portId) => {
        map[portId] = sortPriceCompareCards(map[portId], defaultContractId);
    });

    return map;
};

interface UseMappedPriceCompareConfig extends GetPricesParams {
    defaultContractId?: string;
    enabled: boolean;
}

export const useMappedPriceCompare = ({ defaultContractId, enabled, ...params }: UseMappedPriceCompareConfig) => {
    const { data, isPending, isFetching } = useQuery({
        ...queries.priceCompare.items(params),
        enabled,
        placeholderData: keepPreviousData,
        staleTime: Infinity,
    });

    const supplierIds = useMemo(() => {
        if (!data) {
            return [];
        }
        return Array.from(new Set(data.prices.map((price) => price.supplierId))).sort();
    }, [data]);

    const contractsQuery = useQuery({
        ...queries.contracts.pagination({
            page: 0,
            supplierIds,
            states: [ContractState.ACTIVE],
        }),
        enabled: supplierIds.length > 0,
        select: (page) => page.content,
    });

    const contractsById = useMemo(() => {
        return (contractsQuery.data ?? []).reduce<Record<string, ContractMessage>>((result, contract) => {
            result[contract.id] = contract;
            return result;
        }, {});
    }, [contractsQuery.data]);

    if (!data) {
        return {
            isPending,
            isFetching,
            priceMap: null,
            contractsById,
        };
    }

    return {
        isPending,
        isFetching,
        priceMap: makePriceMap(data.prices, defaultContractId),
        contractsById,
    };
};
